// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  brand: "#D04638",
  brandSoft: "#FBE8E5",
  accent: "#E08A1E",
  accentSoft: "#FBEFD9",
  info: "#2A75B0",
  infoSoft: "#E2EDF7",
  success: "#2C8D5E",
  successSoft: "#DEEEE5",

  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  neutral: "#6B6B6B",
  neutralSoft: "#ECEAE2",

  podium: {
    gold: "#D4A017",
    goldSoft: "#FBF1D2",
    silver: "#9CA3AF",
    silverSoft: "#EEF1F4",
    bronze: "#B87333",
    bronzeSoft: "#F6E4D0",
  },
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes ranking-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(208, 70, 56, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(208, 70, 56, 0); }
    100% { box-shadow: 0 0 0 0 rgba(208, 70, 56, 0); }
  }

  @keyframes ranking-rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ranking-cashier .MuiOutlinedInput-root {
    background: #fff;
    border-radius: 10px;
  }
  .ranking-cashier .MuiOutlinedInput-input {
    padding: 12px 14px;
    font-family: 'Geist', sans-serif;
    font-weight: 500;
  }
  .ranking-cashier .MuiInputLabel-outlined {
    background: #fff;
    padding: 0 4px;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 28px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1920,
  margin: "0 auto",
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 2,
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  pill: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 16px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    cursor: active ? "default" : "pointer",
    userSelect: "none",
  }),
  pillDot: (loading) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: loading ? colors.accent : colors.success,
    animation: loading ? "ranking-pulse 1.2s infinite" : "ranking-pulse 1.8s infinite",
  }),
  iconButton: (active) => ({
    background: active ? colors.text : colors.surface,
    color: active ? colors.surface : colors.textMuted,
    border: `1px solid ${active ? colors.text : colors.border}`,
    borderRadius: 10,
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Filtros
// ─────────────────────────────────────────────────────────────────────────────
export const filters = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "18px 22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  group: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: colors.textSoft,
    textTransform: "uppercase",
  },
  inputs: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  helper: {
    fontSize: 13,
    color: colors.textMuted,
  },
  helperStrong: {
    color: colors.text,
    fontWeight: 600,
    fontFamily: fonts.mono,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textSoft,
    textTransform: "uppercase",
    marginBottom: 4,
    display: "block",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: 180,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  button: (loading) => ({
    background: loading ? "#9C9C9C" : colors.brand,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "11px 22px",
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: loading ? "default" : "pointer",
    transition: "background 0.15s ease, transform 0.05s ease",
    height: 42,
  }),
  buttonGhost: {
    background: "transparent",
    color: colors.textMuted,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: "10px 14px",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    cursor: "pointer",
    height: 42,
  },
  clearIcon: {
    background: "transparent",
    border: "none",
    color: colors.textSoft,
    cursor: "pointer",
    padding: "4px 6px",
    fontSize: 14,
    lineHeight: 1,
    fontWeight: 700,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Delta (comparação)
// ─────────────────────────────────────────────────────────────────────────────
export const delta = {
  badge: (state) => {
    const map = {
      up: { bg: colors.goodSoft, color: colors.good },
      down: { bg: colors.badSoft, color: colors.bad },
      flat: { bg: colors.neutralSoft, color: colors.neutral },
    };
    const c = map[state] || map.flat;
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      borderRadius: 6,
      background: c.bg,
      color: c.color,
      fontSize: 12,
      fontWeight: 700,
      fontFamily: fonts.mono,
      letterSpacing: "0.01em",
      whiteSpace: "nowrap",
    };
  },
  badgeArrow: {
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
  },
  hint: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  hintText: {
    fontSize: 12,
    color: colors.textSoft,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// KPIs
// ─────────────────────────────────────────────────────────────────────────────
export const kpis = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  card: (i) => {
    const accents = [colors.brand, colors.accent, colors.info, colors.success, colors.neutral, colors.brand];
    return {
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${accents[i % accents.length]}`,
      borderRadius: 14,
      padding: "16px 18px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      animation: "ranking-rise 0.32s ease",
    };
  },
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textSoft,
    textTransform: "uppercase",
  },
  value: {
    fontSize: 30,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.025em",
    lineHeight: 1.05,
    color: colors.text,
  },
  unit: {
    fontSize: 16,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 4,
  },
  hint: {
    fontSize: 13,
    color: colors.textMuted,
    fontWeight: 500,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Pódio
// ─────────────────────────────────────────────────────────────────────────────
export const podium = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 28px 28px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textMuted,
  },
  empty: {
    padding: "60px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 15,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 1fr",
    alignItems: "end",
    gap: 16,
  },
  spot: (place) => {
    const map = {
      1: { color: colors.podium.gold, soft: colors.podium.goldSoft, height: 240, order: 2 },
      2: { color: colors.podium.silver, soft: colors.podium.silverSoft, height: 200, order: 1 },
      3: { color: colors.podium.bronze, soft: colors.podium.bronzeSoft, height: 170, order: 3 },
    };
    const cfg = map[place];
    return {
      background: cfg.soft,
      border: `1px solid ${cfg.color}55`,
      borderTop: `4px solid ${cfg.color}`,
      borderRadius: 14,
      padding: "20px 18px 18px",
      minHeight: cfg.height,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      order: cfg.order,
      animation: "ranking-rise 0.42s ease",
      position: "relative",
    };
  },
  medal: (place) => {
    const sizes = { 1: 56, 2: 46, 3: 42 };
    return {
      width: sizes[place],
      height: "auto",
      marginBottom: 10,
      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.18))",
    };
  },
  placeTag: (place) => {
    const map = {
      1: colors.podium.gold,
      2: colors.podium.silver,
      3: colors.podium.bronze,
    };
    return {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.16em",
      color: map[place],
      marginBottom: 6,
      textTransform: "uppercase",
    };
  },
  name: (place) => ({
    fontSize: place === 1 ? 18 : 16,
    fontWeight: 700,
    color: colors.text,
    textAlign: "center",
    lineHeight: 1.2,
    marginBottom: 4,
    wordBreak: "break-word",
  }),
  operator: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSoft,
    fontFamily: fonts.mono,
    marginBottom: 12,
  },
  metrics: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    width: "100%",
    paddingTop: 10,
    borderTop: `1px dashed ${colors.borderStrong}`,
  },
  metricRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 8,
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: colors.textMuted,
    textTransform: "uppercase",
  },
  metricValue: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: colors.text,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Tabela
// ─────────────────────────────────────────────────────────────────────────────
export const tableSection = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "20px 24px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  count: {
    fontSize: 13,
    color: colors.textMuted,
    fontFamily: fonts.mono,
  },
  scroll: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontVariantNumeric: "tabular-nums",
  },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textSoft,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bg,
    whiteSpace: "nowrap",
  },
  thRight: {
    textAlign: "right",
  },
  tr: (i) => ({
    background: i % 2 === 0 ? colors.surface : "#FAF8F2",
    transition: "background 0.15s ease",
  }),
  td: {
    padding: "14px",
    fontSize: 14,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: "nowrap",
  },
  tdRight: {
    textAlign: "right",
    fontFamily: fonts.mono,
    fontWeight: 600,
  },
  rank: (place) => {
    const map = {
      1: { bg: colors.podium.goldSoft, color: colors.podium.gold },
      2: { bg: colors.podium.silverSoft, color: colors.podium.silver },
      3: { bg: colors.podium.bronzeSoft, color: colors.podium.bronze },
    };
    const c = map[place] || { bg: colors.neutralSoft, color: colors.textMuted };
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 32,
      height: 26,
      padding: "0 8px",
      borderRadius: 6,
      background: c.bg,
      color: c.color,
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: "0.02em",
    };
  },
  empty: {
    padding: "40px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 15,
  },
  bar: {
    position: "relative",
    height: 4,
    width: 90,
    background: colors.border,
    borderRadius: 2,
    overflow: "hidden",
    display: "inline-block",
    verticalAlign: "middle",
    marginLeft: 10,
  },
  barFill: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: `linear-gradient(90deg, ${colors.brand}, ${colors.accent})`,
    transition: "width 0.5s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Vendas por hora (chart + tabela)
// ─────────────────────────────────────────────────────────────────────────────
export const hourly = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 26px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 6,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textMuted,
  },
  peakBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 999,
    background: colors.brandSoft,
    color: colors.brand,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    fontFamily: fonts.mono,
  },
  toggle: {
    display: "inline-flex",
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: 3,
  },
  toggleBtn: (active) => ({
    background: active ? colors.surface : "transparent",
    color: active ? colors.text : colors.textMuted,
    border: "none",
    borderRadius: 999,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    fontFamily: fonts.body,
    cursor: "pointer",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  chartWrap: {
    margin: "16px 0 6px",
    overflowX: "auto",
  },
  chart: {
    width: "100%",
    display: "block",
  },
  empty: {
    padding: "32px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 14,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontVariantNumeric: "tabular-nums",
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textSoft,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bg,
    whiteSpace: "nowrap",
  },
  thRight: {
    textAlign: "right",
  },
  tr: (i, peak) => ({
    background: peak
      ? colors.brandSoft
      : i % 2 === 0
      ? colors.surface
      : "#FAF8F2",
  }),
  td: {
    padding: "11px 12px",
    fontSize: 13.5,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: "nowrap",
  },
  tdRight: {
    textAlign: "right",
    fontFamily: fonts.mono,
    fontWeight: 600,
  },
  tdHora: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: colors.text,
  },
  peakChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: colors.brand,
    color: "#fff",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.1em",
    padding: "2px 6px",
    borderRadius: 4,
    marginLeft: 8,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sumário lateral / Glossário
// ─────────────────────────────────────────────────────────────────────────────
export const summary = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "18px 22px 20px",
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: "0 0 14px",
  },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  item: {
    borderLeft: `3px solid ${colors.borderStrong}`,
    padding: "4px 0 4px 14px",
  },
  itemLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: colors.text,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  itemValue: {
    fontSize: 14,
    color: colors.textMuted,
    lineHeight: 1.4,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function compareState(value, baseline) {
  if (value == null || baseline == null) return null;
  if (value > baseline) return "above";
  if (value < baseline) return "below";
  return "equal";
}
